'use client';

import React from "react";
import Link from "next/link";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/accordion/accordion";
import { getSectionSpacingClasses } from "@/components/sections/utils/spacing";
import { cn } from "@/lib/utils";
import { captureEvent } from "@/lib/analytics";
import type { Blocks } from "@/types/cms";

// Loosely-typed rich text node coming from the CMS (Lexical/Slate-style).
// Only the fields the renderer reads are modeled; everything is optional.
interface RichTextNode {
  type?: string;
  text?: string;
  url?: string;
  format?: string;
  level?: number;
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  children?: RichTextNode[];
}

// Treat URLs that start with "/" (and aren't protocol-relative "//...") as
// internal so they use Next's client-side router instead of full-page loads.
const isInternalUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  const trimmed = url.trim();
  return trimmed.startsWith("/") && !trimmed.startsWith("//");
};
import styles from "./accordionSection.module.css";

// Define the type for a single FAQ item based on CMS structure
interface FaqItem {
  id: number;
  documentId?: string;
  question: string;
  answer: Blocks | string; // Blocks field for rich text
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

// Support both accordion section from CMS and standalone FAQ array
interface AccordionSectionProps {
  data:
    | {
        content?: Blocks; // Rich text content for section heading/description
        faqs?: FaqItem[]; // Direct array from accordion section
        // New layout controls from CMS (see documentation/payload.md)
        sectionWidth?: "xs" | "sm" | "md" | "lg" | "full";
        spaceTop?: string;
        spaceBottom?: string;
      }
    | {
        content?: Blocks; // Rich text content for section heading/description
        faqs: {
          data: FaqItem[]; // Legacy format for backward compatibility
        };
        // New layout controls from CMS (see documentation/payload.md)
        sectionWidth?: "xs" | "sm" | "md" | "lg" | "full";
        spaceTop?: string;
        spaceBottom?: string;
      };
}

// Function to render rich text blocks from the CMS
const renderRichText = (blocks: RichTextNode[]): React.ReactNode => {
  if (!Array.isArray(blocks)) return null;

  // Helper function to render inline content with links, bold, italic, etc.
  const renderInlineContent = (children: RichTextNode[] | undefined, accentClass?: string): React.ReactNode => {
    if (!Array.isArray(children)) return null;

    return children.map((child: RichTextNode, childIndex: number) => {
      // Handle inline links — internal URLs use next/link for SPA navigation;
      // external URLs open in a new tab with `rel="noopener"` protection.
      if (child.type === 'link') {
        if (isInternalUrl(child.url)) {
          return (
            <Link key={childIndex} href={child.url ?? ""} className={styles.link}>
              {renderInlineContent(child.children, accentClass)}
            </Link>
          );
        }
        return (
          <a
            key={childIndex}
            href={child.url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            {renderInlineContent(child.children, accentClass)}
          </a>
        );
      }

      // Handle accent font for italics in headers
      if (child.italic && accentClass) {
        return <span key={childIndex} className={accentClass}>{child.text}</span>;
      }

      // Handle text formatting
      if (child.bold && child.italic) {
        return <strong key={childIndex}><em className={styles.inlineBoldItalic}>{child.text}</em></strong>;
      }
      if (child.bold) {
        return <strong key={childIndex} className={styles.inlineBold}>{child.text}</strong>;
      }
      if (child.italic) {
        return <em key={childIndex} className={styles.inlineItalic}>{child.text}</em>;
      }
      if (child.code) {
        return <code key={childIndex} className={styles.inlineCode}>{child.text}</code>;
      }
      if (child.underline) {
        return <u key={childIndex}>{child.text}</u>;
      }
      if (child.strikethrough) {
        return <s key={childIndex}>{child.text}</s>;
      }

      // Handle plain text
      return child.text || '';
    });
  };

  return blocks.map((block: RichTextNode, index: number) => {
    switch (block.type) {
      case 'paragraph':
        return (
          <p key={index} className={`body-lg ${styles.paragraph}`}>
            {renderInlineContent(block.children)}
          </p>
        );
      case 'link':
        // Handle block-level links with the same internal-vs-external split.
        return (
          <p key={index} className={styles.paragraph}>
            {isInternalUrl(block.url) ? (
              <Link
                href={block.url ?? ""}
                className={styles.link}
              >
                {renderInlineContent(block.children)}
              </Link>
            ) : (
              <a
                href={block.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {renderInlineContent(block.children)}
              </a>
            )}
          </p>
        );
      case 'list': {
        const ListTag = block.format === 'ordered' ? 'ol' : 'ul';
        const listClass = [
          'body-lg',
          styles.list,
          block.format === 'ordered' ? styles.listOrdered : styles.listUnordered,
        ].join(' ');
        return (
          <ListTag key={index} className={listClass}>
            {block.children?.map((listItem: RichTextNode, listIndex: number) => (
              <li key={listIndex} className={`body-lg ${styles.listItem}`}>
                {renderInlineContent(listItem.children)}
              </li>
            ))}
          </ListTag>
        );
      }
      case 'heading': {
        let accentClass: string | undefined;
        switch (block.level) {
          case 1: accentClass = "accent-3xl"; break;
          case 2: accentClass = "accent-2xl"; break;
          case 3: accentClass = "accent-xl"; break;
          case 4: accentClass = "accent-lg"; break;
          case 5: accentClass = "accent-md"; break;
          case 6: accentClass = "accent-sm"; break;
        }
        const headingContent = renderInlineContent(block.children, accentClass);
        switch (block.level) {
          case 1:
            return <h1 key={index} className={`accent-3xl ${styles.heading1}`}>{headingContent}</h1>;
          case 2:
            return <h2 key={index} className={`accent-2xl ${styles.heading2}`}>{headingContent}</h2>;
          case 3:
            return <h3 key={index} className={`accent-xl ${styles.heading3}`}>{headingContent}</h3>;
          case 4:
            return <h4 key={index} className={`accent-lg ${styles.heading4}`}>{headingContent}</h4>;
          case 5:
            return <h5 key={index} className={`accent-md ${styles.heading5}`}>{headingContent}</h5>;
          case 6:
            return <h6 key={index} className={`accent-sm ${styles.heading6}`}>{headingContent}</h6>;
          default:
            return <p key={index} className={`body-md ${styles.headingFallback}`}>{headingContent}</p>;
        }
      }
      case 'quote':
        return (
          <blockquote key={index} className={`body-md ${styles.blockquote}`}>
            {renderInlineContent(block.children)}
          </blockquote>
        );
      case 'code':
        return (
          <pre key={index} className={styles.codeBlock}>
            <code>{block.children?.[0]?.text || ''}</code>
          </pre>
        );
      default:
        return (
          <p key={index} className={`body-md ${styles.paragraph}`}>
            {renderInlineContent(block.children) || block.children?.[0]?.text || ''}
          </p>
        );
    }
  });
};

const extractPlainText = (node: unknown): string => {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (typeof node === "object") {
    const n = node as RichTextNode;
    if (typeof n.text === "string") return n.text;
    if (Array.isArray(n.children)) return n.children.map(extractPlainText).join("");
  }
  return "";
};

const slugifyId = (raw: string): string => {
  return raw
    .toLowerCase()
    .trim()
    .replace(/^#/, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const AccordionSection = ({ data }: AccordionSectionProps) => {
  if (!data) return null;
  const { faqs } = data;
  const content = data.content as RichTextNode[] | undefined;

  // Handle both accordion section format and legacy FAQ format
  const faqItems: FaqItem[] = Array.isArray(faqs)
    ? faqs
    : (faqs as { data?: FaqItem[] } | undefined)?.data || [];

  if (!faqItems.length) return null;

  // `data` carries extra CMS fields not modeled in the prop union.
  const dataRecord = data as Record<string, unknown>;

  // Match CardSection: use shared spacing + width tokens
  const spacing = getSectionSpacingClasses(dataRecord);
  const widthToken = dataRecord?.sectionWidth as
    | "xs"
    | "sm"
    | "md"
    | "lg"
    | "full"
    | undefined;
  const widthClass = widthToken ? `w-${widthToken}` : undefined;

  // Read the CMS custom ID field (accordionSection_customID in the schema)
  const rawCustomId = dataRecord?.accordionSection_customID as
    | string
    | undefined;
  const customId =
    typeof rawCustomId === "string" && rawCustomId.startsWith("#")
      ? rawCustomId.slice(1)
      : rawCustomId;

  // Derive a stable section id from the section title (first heading in `content`).
  // This allows nav links like `#pricing` / `#faq` to scroll to this section.
  const titleFromContent =
    Array.isArray(content)
      ? (() => {
          const headingBlock = content.find((b: RichTextNode) => b?.type === "heading");
          if (!headingBlock) return "";
          return extractPlainText({ children: headingBlock.children });
        })()
      : "";
  const derivedId = titleFromContent ? slugifyId(titleFromContent) : "";
  const sectionId = customId || derivedId || "faq";

  return (
    <section
      id={sectionId}
      className={['section', spacing].filter(Boolean).join(" ")}
    >
      <div
        className={['section-container', widthClass]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={styles.inner}>
          {/* Render section content (heading, description, etc.) */}
          {content && Array.isArray(content) && (
            <div className={styles.contentHeader}>
              {renderRichText(content)}
            </div>
          )}
          {/* Use list semantics and proper heading order within items */}
          <Accordion
            type="single"
            collapsible
            className={styles.accordionRoot}
            onValueChange={(v) =>
              captureEvent("faq_toggled", { action: v ? "open" : "close", item_value: v })
            }
          >
            {faqItems.map((item: FaqItem) => (
              <AccordionItem key={item.id} value={`item-${item.id}`}>
                {/* Visual heading lives inside the trigger button so it keeps
                    its accent typography while still being a single
                    clickable <button> — avoids the asChild foot-gun that
                    silently broke toggle behavior. */}
                <AccordionTrigger className={cn(styles.triggerAlign, "body-lg", styles.triggerHeading)}>
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className={styles.answer}>
                  {typeof item.answer === 'string' ? (
                    <p>{item.answer}</p>
                  ) : Array.isArray(item.answer) ? (
                    <div>{renderRichText(item.answer as RichTextNode[])}</div>
                  ) : (
                    <p>No answer available</p>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default AccordionSection;
